/* eslint-disable max-classes-per-file */
/**
 * !IMPORTANT: These classes are used in the CSM Widget Script.js entrypoint
 * Please don't add any Atlassian/npm packages/dependencies or feature gates / analytics etc none of that is supported in
 * the script.js entrypoint and they significantly bloat the bundle size.
 */
import type { CSMErrorType } from '@atlassian/customer-service-management-widget-sdk-types/CSMErrorType';
import type { WidgetConfig } from '@atlassian/customer-service-management-widget-sdk-types/WidgetConfig';

import { fetchWithRetry } from '../../common/utils/fetch-with-retry';
import type { AnonymousAuthProviderType, AuthProvider } from '../../types';

declare global {
	interface Window {
		/**
		 * Platform feature flags resolver
		 */
		__PLATFORM_FEATURE_FLAGS__?: {
			booleanResolver?: (key: string) => boolean;
		};
	}
}

type WidgetAccessResponse = {
	hasAccess: boolean;
	anonAuthToken: string | null;
	/**
	 * Whether the end user is an anonymous (logged-out) user, as determined by
	 * the convo-ai backend.
	 *
	 * Distinct from `anonAuthToken`: that field is only populated for *unverified*
	 * anonymous users (it is the freshly-minted session secret/id), and is
	 * therefore null for both logged-in users AND already-verified anonymous
	 * users. `isUserAnonymous` reports the underlying user type and is therefore
	 * `true` for both verified and unverified anonymous users, and `false` for
	 * logged-in users.
	 */
	isUserAnonymous: boolean;
};

type WidgetAccessErrorResponse = {
	errors?: string[];
};

export type VerifyAccessMetrics = {
	authenticateStartTime: number;
	authenticateEndTime: number;
	accessCheckStartTime: number;
	accessCheckEndTime: number;
};

export type VerifyAccessResult<P extends AuthProvider | null> = {
	provider: P | AnonymousAuthProvider | SessionAuthProvider | null;
	metrics: VerifyAccessMetrics;
	errors: CSMError[];
};

const perfNow = () => {
	try {
		return performance.now();
	} catch {
		return 0;
	}
};

const ANONYMOUS_AUTH_HEADER = 'X-Anonymous-Auth';
const AI_FEATURE_DISABLED_ERROR_CODE = 'convoai.rest.ai.feature.toggle.off';
const AI_FEATURE_DISABLED_ERROR_MESSAGE = 'AI features are not enabled on your site';

const getMessage = (error: unknown) => {
	if (typeof error === 'string') {
		return error;
	}

	if (error instanceof Error) {
		return error.message;
	}

	return 'Unknown error';
};

/**
 * Error class for CSM Embed widget failures
 */
export class CSMError extends Error {
	public readonly isNetworkError: boolean;

	constructor(
		/**
		 * The error message or Error object
		 */
		error: unknown,
		/**
		 * The type of error
		 */
		public readonly type: CSMErrorType,
		/** Additional metadata about the error */
		public readonly attributes?: {
			traceId: string | null;
			status: number | null;
			authType?: 'oauth' | 'anonymous' | 'session';
		},
	) {
		super(getMessage(error));
		this.name = 'CSMError';
		this.isNetworkError = isNetworkError(error);
	}
}

/**
 * Determines if an error is a network error (e.g., failed to fetch due to no connectivity)
 */
export const isNetworkError = (error: unknown): boolean => {
	if (error instanceof TypeError) {
		const message = error.message.toLowerCase();
		return (
			message.includes('failed to fetch') ||
			message.includes('network') ||
			message.includes('networkerror') ||
			message.includes('load failed')
		);
	}
	return false;
};

/**
 * This class is used in the CSM Widget Script.js entrypoint
 * Please don't add any atlassian packages or feature gates / analytics etc none of that is supported in
 * the script.js entrypoint and they significantly bloat the bundle size.
 */
export class AnonymousAuthProvider implements AnonymousAuthProviderType {
	readonly type = 'anonymous';
	private anonAuthToken: string | null = null;

	constructor({ anonAuthToken }: { anonAuthToken: string | null }) {
		this.anonAuthToken = anonAuthToken;
	}

	async getAuthHeaders(): Promise<Headers> {
		if (!this.anonAuthToken) {
			throw new Error('Anonymous auth invalid');
		}

		return new Headers({
			[ANONYMOUS_AUTH_HEADER]: this.anonAuthToken,
		});
	}

	invalidate(): void {
		this.anonAuthToken = null;
	}

	serialise(): {
		readonly type: 'anonymous';
		readonly anonAuthToken: string | null;
	} {
		return {
			type: this.type,
			anonAuthToken: this.anonAuthToken,
		} as const;
	}
}

/**
 * This class is used in the CSM Widget Script.js entrypoint
 * Please don't add any atlassian packages or feature gates / analytics etc none of that is supported in
 * the script.js entrypoint and they significantly bloat the bundle size.
 */
export class SessionAuthProvider implements AuthProvider {
	readonly type = 'session';

	getAuthHeaders(): Promise<null> {
		return Promise.resolve(null);
	}

	invalidate(): void {}

	serialise(): {
		readonly type: 'session';
	} {
		return {
			type: this.type,
		} as const;
	}
}

type AuthenticateFn = NonNullable<Required<WidgetConfig>['authenticate']>;

/**
 * This external oauth provider is designed to use a provided authenticate function to fetch an oauth access token.
 * Expected to be used with the `authenticate` function configured by the customer/host site.
 */
export class ExternalOAuthProvider implements AuthProvider {
	readonly type = 'oauth' as const;
	private accessToken: string | null = null;
	private pendingFetch: Promise<string> | null = null;
	private authenticate: AuthenticateFn;

	constructor(authenticate: AuthenticateFn) {
		this.authenticate = async () => {
			try {
				return await authenticate();
			} catch (error) {
				console.error('[CSM Widget] The provided authenticate function threw an error:', error);
				throw new CSMError(
					'The provided authenticate function threw an error',
					'authenticate_fn_exception',
				);
			}
		};
	}

	async getAuthHeaders(): Promise<Headers> {
		if (!this.accessToken) {
			if (!this.pendingFetch) {
				this.pendingFetch = this.fetchAccessToken();
			}

			// ALL concurrent calls await the SAME promise
			try {
				this.accessToken = await this.pendingFetch;
			} finally {
				this.pendingFetch = null;
			}
		}

		return new Headers({
			Authorization: `Bearer ${this.accessToken}`,
		});
	}

	private async fetchAccessToken() {
		try {
			const result: unknown = await this.authenticate();

			if (result === null) {
				throw new CSMError('Authenticate function returned null.', 'authenticate_fn_exception');
			}

			if (typeof result !== 'object') {
				throw new CSMError(
					'Authenticate function did not return an object',
					'authenticate_fn_exception',
				);
			}

			if ('access_token' in result) {
				if (typeof result.access_token !== 'string') {
					throw new CSMError(
						'Authenticate function returned an access token that is not a string',
						'authenticate_fn_exception',
					);
				}
				return result.access_token;
			}

			if ('accessToken' in result) {
				if (typeof result.accessToken !== 'string') {
					throw new CSMError(
						'Authenticate function returned an access token that is not a string',
						'authenticate_fn_exception',
					);
				}
				return result.accessToken;
			}

			throw new CSMError(
				'Authenticate function did not return an access token',
				'authenticate_fn_exception',
			);
		} catch (error) {
			if (error instanceof CSMError) {
				throw error;
			}
			throw new CSMError(
				'Something went wrong when calling the authenticate function',
				'authenticate_fn_exception',
			);
		}
	}

	invalidate(): void {
		this.accessToken = null;
	}

	serialise(): {
		readonly type: 'oauth';
		readonly accessToken: string | null;
	} {
		return {
			type: 'oauth',
			accessToken: this.accessToken,
		} as const;
	}
}

/**
 * This class is used in the CSM Widget Script.js entrypoint
 * Please don't add any atlassian packages or feature gates / analytics etc none of that is supported in
 * the script.js entrypoint and they significantly bloat the bundle size.
 */
export class CSMWidgetAuthProviderService {
	static async verifyAccess<P extends AuthProvider | null>(
		/**
		 * The help center Ari to check access for
		 */
		helpCenterAri: string,
		/**
		 * The authentication provider to use for the widget
		 */
		authProvider: P | null,
		/**
		 * The anonymous authentication provider to use for the widget
		 */
		anonymousAuthProvider: AnonymousAuthProvider | null,
		/**
		 * Configure the Base URL if used outside of the context of an Atlassian hosted application
		 */
		baseUrl: string = '',
	): Promise<VerifyAccessResult<P>> {
		const metrics: VerifyAccessMetrics = {
			authenticateStartTime: 0,
			authenticateEndTime: 0,
			accessCheckStartTime: 0,
			accessCheckEndTime: 0,
		};
		const errors: CSMError[] = [];

		let status: number | null = null;
		let traceId: string | null = null;

		const convertToCSMError = (
			error: unknown,
			authType?: 'oauth' | 'anonymous' | 'session',
		): CSMError => {
			if (error instanceof CSMError) {
				return error;
			}
			if (error instanceof Error) {
				return new CSMError(error, 'auth_failed', {
					traceId,
					status,
					authType,
				});
			}
			return new CSMError('Exception caught in access check', 'auth_failed', {
				traceId,
				status,
				authType,
			});
		};

		try {
			const headers = new Headers({
				'x-product': 'csm',
				'x-experience-id': 'csm-ai',
			});

			let providerWithAuthHeaders: AnonymousAuthProvider | SessionAuthProvider | P | null = null;

			const providers = [authProvider, anonymousAuthProvider].filter(
				(provider) => provider !== null,
			);

			metrics.authenticateStartTime = perfNow();

			for (const provider of providers) {
				try {
					const authHeaders = await provider.getAuthHeaders();
					if (!authHeaders) {
						continue;
					}
					authHeaders.forEach((value, key) => {
						headers.set(key, value);
					});
					providerWithAuthHeaders = provider;
					break;
				} catch (error) {
					errors.push(convertToCSMError(error, provider.type));
					continue;
				}
			}

			metrics.authenticateEndTime = perfNow();
			metrics.accessCheckStartTime = perfNow();

			const res = await fetchWithRetry(
				`${baseUrl}/gateway/api/assist/csm/v2/widgets/access?helpCenterAri=${helpCenterAri}`,
				{
					headers,
				},
			);

			traceId = res.headers.get('x-trace-id');
			status = res.status;

			if (!res.ok) {
				metrics.accessCheckEndTime = perfNow();

				if (status === 405) {
					const error = new CSMError('WAF blocked access check request', 'waf_blocked', {
						traceId,
						status,
					});

					return {
						provider: null,
						metrics,
						errors: [...errors, error],
					};
				}

				if (status === 400) {
					const accessErrorResponse: WidgetAccessErrorResponse | null = await res
						.json()
						.catch(() => null);

					if (accessErrorResponse?.errors?.includes(AI_FEATURE_DISABLED_ERROR_CODE)) {
						const error = new CSMError(AI_FEATURE_DISABLED_ERROR_MESSAGE, 'ai_feature_toggle_off', {
							traceId,
							status,
							authType: providerWithAuthHeaders?.type,
						});

						return {
							provider: null,
							metrics,
							errors: [...errors, error],
						};
					}
				}

				const error = new CSMError(
					`Failed to verify access: ${res.status} ${res.statusText}`,
					'auth_failed',
					{
						traceId,
						status,
						authType: providerWithAuthHeaders?.type,
					},
				);

				return {
					provider: null,
					metrics,
					errors: [...errors, error],
				};
			}

			const { hasAccess, anonAuthToken, isUserAnonymous }: WidgetAccessResponse = await res.json();
			metrics.accessCheckEndTime = perfNow();

			if (!hasAccess) {
				return {
					provider: null,
					metrics,
					errors,
				};
			}

			if (isUserAnonymous) {
				if (providerWithAuthHeaders?.type === 'anonymous') {
					// existing `providerWithAuthHeaders` is an AnonymousAuthProvider
					return {
						provider: providerWithAuthHeaders,
						metrics,
						errors,
					};
				}

				// User is truly anonymous (i.e. not logged in) with access allowed on support site
				if (anonAuthToken) {
					// first time users
					return {
						provider: new AnonymousAuthProvider({ anonAuthToken }),
						metrics,
						errors,
					};
				}
			}

			if (!isUserAnonymous && providerWithAuthHeaders?.type === 'oauth') {
				// User is logged in through OAuth
				return {
					provider: providerWithAuthHeaders,
					metrics,
					errors,
				};
			}

			// use SessionAuthProvider as final fallback
			return {
				provider: new SessionAuthProvider(),
				metrics,
				errors,
			};
		} catch (error) {
			metrics.authenticateEndTime = metrics.authenticateEndTime || perfNow();
			metrics.accessCheckEndTime = metrics.accessCheckEndTime || perfNow();

			return {
				provider: null,
				metrics,
				errors: [...errors, convertToCSMError(error)],
			};
		}
	}
}
